import "./filterForm.css";
import React, { useEffect, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete, {
  AutocompleteChangeReason,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import { getFilterDropdowns } from "../../API/getFilterDropdowns";
import {
  saveFilterExceptionCodeData,
  saveAllExceptionCodeData,
  saveFilterDateData,
  saveFilterStatusData,
  saveFilterValueData,
  saveOveragesFilterForm,
  IFilterForm,
  TTerminal,
  IFilterDropdown,
  saveShortageExceptionCodeData,
  saveShortageStatusMaster,
  saveMyAssignmentStatusMaster,
  saveDamageStatusMaster,
  saveDamageExceptionCodeData,
  saveOnHandDeliveryExceptionCodeData,
  saveOnHandDeliveryStatusMaster,
  saveMisdeliveryExceptionCodeData,
  saveMisdeliveryStatusMaster,
} from "../../AppState/overagesFilterFormActions";
import {
  ALL_OSD_TYPES,
  APP,
  DType,
  EAutoSearchFieldName,
  ETab,
  FORM_STYLES,
  MAX_LENGTH_MANIFEST_NUMBER,
  MAX_LENGTH_PRO,
  FILTER_SHOW_OD400,
  OSD_TYPE_TO_TAB_MAPPING,
  REDUX_RESET_ACTIONS,
  FILTER_SHOW_DRIVER,
  EXCEPTION_CODE_TYPE_TO_TAB_MAPPING,
  STATUS_TYPE_TO_TAB_MAPPING,
  PRO_MASTER_SEARCH_EXCEPTION,
  EXCEPTION_SEARCH_DESTINATION_TERMINAL_FOR,
  S_GRID_PROPS,
  XL_GRID_PROPS,
} from "../../constants";
import { getMasterProNumberAPI } from "../../API/getMasterProNumber";
import { getMasterOsdNumberAPI } from "../../API/getMasterOsdNumber";
import { exceptionCodesMasterApi } from "../../API/getExceptionCodesMaster";
import { debounce } from "lodash";
import { saveMyAssignmentsFilterForm } from "../../AppState/myAssignmentsFilterFormActions";
import {
  saveClearedPageObject,
  saveDamagesPageObject,
  saveMisdeliveryPageObject,
  saveMyAssignmentPageObject,
  saveOnHandDeliveryPageObject,
  saveOverageListPageObject,
  saveSearchExceptionPageObject,
  saveShortagesPageObject,
} from "../../AppState/DataTableAction";
import { resetState } from "../../AppState/resetStateActions";
import { GridPaginationModel } from "@mui/x-data-grid";
import {
  handlePaste,
  handleTextFieldKeyDown,
  noOptionsText,
} from "../../helpers";
import { saveShortagesFilterForm } from "../../AppState/shortagesFilterFormAction";
import { IMaterUser } from "../../models/user";
import {
  commoditySelector,
  clearingCodesSelector,
  currentTerminalSelector,
  dateMasterSelector,
  exceptionCodesSelector,
  filterFromSelector,
  investigationStatusSelector,
  pageSelector,
  tabPositionSelector,
  terminalSelector,
  valueRangeSelector,
} from "../../AppState/selectors";
import { hasAnyEnabledFor } from "../../AppState/selectors/userPermissionsSelectors";
import { saveDamagesFilterForm } from "../../AppState/damagesFilterFormAction";
import CustomIconComponent from "../CustomDropdownIcon";
import { saveOnHandDeliveryFilterForm } from "../../AppState/onHandDeliveryFilterFormAction";
import { saveMisdeliveryFilterForm } from "../../AppState/misdeliveryFilterFormAction";
import {
  saveClearedFilterForm,
  saveExceptionSearchFilterForm,
} from "../../AppState/exceptionSearchFilterFromAction";
import TerminalAutocomplete from "./TerminalAutocomplete";
import UserAutocomplete from "./UserAutocomplete";
import { useCommodityMaster } from "../../@hooks/useCommodityMaster";
import { useClearingCodesMaster } from "../../@hooks/useClearingCodesMaster";
import DateRangeFilter from "./DateRangeFilter";

type Props = {
  onFilterSubmit?: () => void;
};
const SearchFilterForm: React.FC<Props> = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  // State for new fields
  const [clearingCode, setClearingCode] = React.useState<string[]>([]);
  const tabPosition: ETab = useSelector(tabPositionSelector);
  const currentTerminalValue = useSelector(currentTerminalSelector);
  const filterForm: IFilterForm = useSelector(filterFromSelector);
  const { loadCommodityOptions } = useCommodityMaster();
  const { loadClearingCodes } = useClearingCodesMaster();
  const pageObject: GridPaginationModel | undefined = useSelector(pageSelector);

  const [date, setDate] = React.useState("");
  const valueRangeMasterData: IFilterDropdown[] =
    useSelector(valueRangeSelector);
  const investigationStatusMasterData: IFilterDropdown[] = useSelector(
    investigationStatusSelector,
  );
  const overagesFilterDateData = useSelector(dateMasterSelector);
  const exceptionCodes = useSelector(exceptionCodesSelector);
  const commodityTypeMaster = useSelector(commoditySelector);
  const clearingCodesMasterData = useSelector(clearingCodesSelector);
  const terminalMaster = useSelector(terminalSelector);
  const userPermissions = useSelector((state: any) => state.userPermissions);

  const hasPermissionForExceptionCode = useCallback(
    (exceptionCode: string): boolean => {
      const permissionMap: Record<string, () => boolean> = {
        OV: () => hasAnyEnabledFor(userPermissions?.overages?.details),
        NB: () => hasAnyEnabledFor(userPermissions?.overages?.details),
        AS: () => hasAnyEnabledFor(userPermissions?.shortages?.details),
        DS: () => hasAnyEnabledFor(userPermissions?.shortages?.details),
        BNF: () => hasAnyEnabledFor(userPermissions?.shortages?.details),
        PR: () => hasAnyEnabledFor(userPermissions?.damages?.details),
        RD: () => hasAnyEnabledFor(userPermissions?.damages?.details),
        RF: () => hasAnyEnabledFor(userPermissions?.damages?.details),
        DD: () => hasAnyEnabledFor(userPermissions?.damages?.details),
        OH: () => hasAnyEnabledFor(userPermissions?.onHandDeliveries?.details),
        MSD: () => hasAnyEnabledFor(userPermissions?.misDeliveries?.details),
      };

      return permissionMap[exceptionCode] ? permissionMap[exceptionCode]() : true;
    },
    [userPermissions],
  );

  const selectedClearingCodes = useMemo(() => {
    if (!clearingCode?.length) return [] as IFilterDropdown[];

    return (clearingCodesMasterData ?? []).filter((opt: IFilterDropdown) =>
      clearingCode.includes(opt.actualValue)
    );
  }, [clearingCode, clearingCodesMasterData]);

  const [tid, setTid] = React.useState<string[]>([]);
  const [did, setDid] = React.useState<string[]>([]);
  const [rTid, setRTid] = React.useState<string[]>([]);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);

  const [cInputValue, setCInputValue] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);

  const [fromDate, setFromDate] = React.useState<string>("");
  const [toDate, setToDate] = React.useState<string>("");

  const [pickupDate, setPickupDate] = React.useState<string>("");
  const [pickupFromDate, setPickupFromDate] = React.useState<string>("");
  const [pickupToDate, setPickupToDate] = React.useState<string>("");

  const onAutoInputChange = (v: string, r: string, type: string = "") => {
    if (r === "input") {
      const setterMap: Record<string, (value: string) => void> = {
        commodity: setCInputValue,
      };

      setterMap[type]?.(v);
    }
  };

  const [exco, setExco] = React.useState<string[]>([]);
  const handleChangeExcep = (event: SelectChangeEvent<typeof exco>) => {
    const {
      target: { value },
    } = event;
    const next = typeof value === "string" ? value.split(",") : value;
    // Ignore codes the user has no permission for so they can never be selected
    setExco(next.filter((code) => hasPermissionForExceptionCode(code)));
  };

  const [ctype, setCtype] = React.useState<string[]>([]);
  const handleChangeCommodityType = (value: string[]) => {
    setCtype(value);
  };

  const isMSD = tabPosition === ETab.misdelivery;
  const isOH = tabPosition === ETab.onHandDelivery;
  const isOv = tabPosition === ETab.overages;
  const isCleared = tabPosition === ETab.cleared;
  const isDashboard = tabPosition === ETab.dashboard;

  const CLEARING_STATUS_OPTIONS = [
    { value: "nonCleared", label: "Open", borderColor: "#FFC051" },
    { value: "cleared", label: "Cleared", borderColor: "#0BC568" },
  ];

  const [clearingStatusFilter, setClearingStatusFilter] = React.useState<string[]>(["uncleared"]);

  const [visibility, setVisibility] = React.useState<string[]>(["nonCleared"]);
  const handleVisibilityChange = (event: SelectChangeEvent<typeof visibility>) => {
    const {
      target: { value },
    } = event;
    setVisibility(typeof value === "string" ? value.split(",") : value);
  };

  const [value, setValue] = React.useState<string[]>([]);

  const handleChangeValue = (event: SelectChangeEvent<any>) => {
    const {
      target: { value },
    } = event;
    setValue(typeof value === "string" ? value.split(",") : value);
  };

  const [status, setStatus] = React.useState<string[]>([]);
  const handleStatusChange = (event: SelectChangeEvent<any>) => {
    const {
      target: { value },
    } = event;
    setStatus(typeof value === "string" ? value.split(",") : value);
  };

  const [description, setDescription] = React.useState<string | null>("");
  const [driverName, setDriverName] = React.useState<string | null>("");
  const [dockWorkerName, setDockWorkerName] = React.useState<string | null>("");
  const [consigneeName, setConsigneeName] = React.useState<string | null>("");
  const [shipperName, setShipperName] = React.useState<string | null>("");
  const [proEntry, setProEntry] = React.useState<string | null>("");
  const [od400ProEntry, setOD400ProEntry] = React.useState<string | null>("");
  const [osdNumber, setOSDNumber] = React.useState<string | null>("");
  const [entryUser, setEntryUser] = React.useState<any>("");
  const [assignUser, setAssignUser] = React.useState<any>("");
  const [assignBy, setAssignBy] = React.useState<any>("");

  const [proMasterData, setProMasterData] = React.useState<string[]>([]);
  const [od400ProMasterData, setOD400ProMasterData] = React.useState<string[]>(
    [],
  );
  const [osdMasterData, setOsdMasterData] = React.useState<string[]>([]);
  type UserState = IMaterUser | null | undefined;
  const [assignUserObject, setAssignUserObject] =
    React.useState<UserState>(null);
  const [entryUserObject, setEntryUserObject] = React.useState<UserState>(null);
  const [assignByObject, setAssignByObject] = React.useState<UserState>(null);

  const onOSDexceptionChange = (
    value: string | null,
    reason?: AutocompleteChangeReason,
  ) => {
    if (reason === "clear") setOsdMasterData([]);
    if (!value || value === "undefined") return;
    setOSDNumber(value);
  };

  const onPROentryChange = (
    value: string | null,
    reason?: AutocompleteChangeReason,
  ) => {
    if (reason === "clear") setProMasterData([]);
    if (!value || value === "undefined") return;
    setProEntry(value);
  };

  const onOD400PROentryChange = (
    value: string | null,
    reason?: AutocompleteChangeReason,
  ) => {
    if (reason === "clear") setOD400ProMasterData([]);
    if (!value || value === "undefined") return;
    setOD400ProEntry(value);
  };

  // Get terminal, Commodity exception codes, values, status Filter masters for Dropdown data API call
  const getSearchFiltersAPI = async (ParamString: string) => {
    if (ParamString === "commodityType") {
      loadCommodityOptions();
    } else if (ParamString === "Exception_Codes") {
      // get Exception code/type for overage list and myAssignment
      exceptionCodesMasterApi({
        dataType:
          EXCEPTION_CODE_TYPE_TO_TAB_MAPPING[
            tabPosition as keyof typeof EXCEPTION_CODE_TYPE_TO_TAB_MAPPING // NOSONAR
          ] ?? "myAssignment",
      })
        .then((results) => {
          const dispatchMap = {
            [ETab.overages]: saveFilterExceptionCodeData,
            [ETab.myAssignments]: saveAllExceptionCodeData,
            [ETab.cleared]: saveAllExceptionCodeData,
            [ETab.dashboard]: saveAllExceptionCodeData,
            [ETab.damages]: saveDamageExceptionCodeData,
            [ETab.shortages]: saveShortageExceptionCodeData,
            [ETab.onHandDelivery]: saveOnHandDeliveryExceptionCodeData,
            [ETab.misdelivery]: saveMisdeliveryExceptionCodeData,
          };

          const action =
            dispatchMap[tabPosition as keyof typeof dispatchMap] || // NOSONAR
            saveAllExceptionCodeData;
          dispatch(action(results));
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      let params: { dataType: string } = {
        dataType: ParamString,
      };
      // store date rage, search value  and status data in redux store.
      getFilterDropdowns(params)
        .then((results) => {
          switch (ParamString) {
            case "overageSearchDateRange": {
              const data = (results ?? []).sort(
                (a: IFilterDropdown, b: IFilterDropdown) =>
                  a.serialNumber! - b.serialNumber!,
              );
              dispatch(saveFilterDateData(data));
              break;
            }
            case "overageSearchValues":
              dispatch(saveFilterValueData(results));
              break;
            case "overageStatus":
              dispatch(saveFilterStatusData(results));
              break;
            case "shortageStatus":
              dispatch(saveShortageStatusMaster(results));
              break;
            case "myAssignmentStatus":
              dispatch(saveMyAssignmentStatusMaster(results));
              break;
            case "damageStatus":
              dispatch(saveDamageStatusMaster(results));
              break;
            case "onHandDeliveryStatus":
              dispatch(saveOnHandDeliveryStatusMaster(results));
              break;
            case "misdeliveryStatus":
              dispatch(saveMisdeliveryStatusMaster(results));
              break;
            default:
              return "";
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  // Event fire on Entry user changes from Filter form and set entry user

  // Call API getMasterProNumberAPI method for get PRO numbers master options to filter Data on grid
  const getProMasterDataAPI = (values: string, proType: string = "normal") => {
    if (values?.length > 1) {
      let osdType: string = "";

      // Optimize osdType assignment based on tabPosition and proType
      if (PRO_MASTER_SEARCH_EXCEPTION.includes(tabPosition)) {
        osdType = exceptionCodes.join(",");
      } else if (proType === DType.od400) {
        if (isOv) {
          osdType = "NB,OV";
        } else if (isMSD) {
          osdType = "MSD";
        } else osdType = "NB,OV,MSD";
      }
      const params: {
        search: string;
        page: number;
        limit: number;
        osdType: string;
        proType: string;
      } = {
        search: values.toString(),
        page: APP.overage.userAPIpageLimit,
        limit: APP.overage.userAPILimit,
        osdType,
        proType,
      };

      getMasterProNumberAPI(params)
        .then((results: any) => {
          setLoading(false);
          if (proType === DType.od400) {
            setOD400ProMasterData(results?.data);
          } else {
            setProMasterData(results?.data);
          }
        })
        .catch((error) => {
          setLoading(false);
        });
    }
  };

  // Helper function to generate terminal list
  const getTerminalList = (
    currentTerminal: TTerminal,
    allTerminals: TTerminal[],
  ) => {
    // If currentTerminal?.region is undefined or null, return all terminals with the same region
    if (!currentTerminal?.region) {
      return allTerminals
        .filter((t) => t.region === currentTerminal.terminalId)
        .map((t) => t.terminalId);
    }

    // Otherwise, return an array containing only the current terminal's terminalId
    return [currentTerminal.terminalId];
  };

  // Memoize columnVisibilityModel, terminal list and exception type
  const terminalList = useMemo(
    () => getTerminalList(currentTerminalValue, terminalMaster),
    [currentTerminalValue, terminalMaster],
  );
  // Memoized destination and reporting based on tabPosition and terminal value
  const destination = useMemo(() => {
    // For shortages, damages and onHandDelivery, destination is terminal list
    if (EXCEPTION_SEARCH_DESTINATION_TERMINAL_FOR.includes(tabPosition))
      return currentTerminalValue?.terminalId === "All" ? [] : terminalList;
    return [];
  }, [tabPosition, currentTerminalValue, terminalList]);

  const reporting = useMemo(() => {
    // For overages, reporting is always terminal list
    if (isOv) {
      return currentTerminalValue?.terminalId === "All" ? [] : terminalList;
    }
    return [];
  }, [currentTerminalValue, terminalList, isOv]);

  // Call API getMasterOsdNumberAPI method for get exception numbers master options to filter Data on grid
  const getOsdMasterDataAPI = async (values: any) => {
    const isCodeEmpty = !exceptionCodes?.length;
    let osdTypes: string[];
    if (isCodeEmpty) {
      osdTypes =
        OSD_TYPE_TO_TAB_MAPPING[tabPosition] ?? ALL_OSD_TYPES;
    } else {
      osdTypes = exceptionCodes;
    }
    if (values?.length > 1) {
      const VISIBILITY_BY_TAB: Partial<Record<ETab, string>> = {
        [ETab.dashboard]: "cleared,nonCleared",
        [ETab.cleared]: "cleared",
      };
      const visibilityParam = VISIBILITY_BY_TAB[tabPosition] ?? "nonCleared";

      let params: any = {
        search: values.toString(),
        page: APP.overage.userAPIpageLimit,
        limit: APP.overage.userAPILimit,
        osdType: osdTypes.join(","),
        destination: tabPosition !== ETab.misdelivery ? destination.join(","): "",
        reporting: reporting.join(","),
        visibility: visibilityParam,
      };
      getMasterOsdNumberAPI(params)
        .then((results: any) => {
          setLoading(false);
          setOsdMasterData(results?.data);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(
    debounce((value, type) => {
      if (type === DType.pro || type === DType.od400) {
        getProMasterDataAPI(value, type);
      } else if (type === DType.osd) {
        getOsdMasterDataAPI(value);
      }
    }, 1000),
    [],
  );

  const onInputChange = (value: any, type: string) => {
    if (value === "undefined") return;

    const setterMap: Record<string, () => void> = {
      [DType.pro]: () => {
        setProEntry(value);
        setProMasterData([]);
      },
      [DType.od400]: () => {
        setOD400ProEntry(value);
        setOD400ProMasterData([]);
      },
      [DType.osd]: () => {
        setOSDNumber(value);
        setOsdMasterData([]);
      },
    };
    setterMap[type]?.();
    setLoading(true);
    debounced(value, type);
  };

  // Action perform on "Clear Filter" button click on Filter form reset all filter form value as initials
  const onFormClear = () => {
    const resetKey = REDUX_RESET_ACTIONS[tabPosition];
    if (resetKey) dispatch(resetState(resetKey));

    if (props.onFilterSubmit) {
      props.onFilterSubmit();
    }
  };

  // Helper function to render text field
  const renderTextField = (
    label: string,
    id: string,
    value: string | null,
    onChange: (value: string) => void,
  ) => (
    <Grid item {...S_GRID_PROPS}>
      <FormControl fullWidth size="small">
        <InputLabel style={FORM_STYLES.LABEL} shrink={true} htmlFor={id}>
          {label}
        </InputLabel>
        <TextField
          autoComplete="off"
          id={id}
          data-testid={id}
          onChange={(e) => onChange(e?.target?.value ?? "")}
          variant="outlined"
          value={value}
        />
      </FormControl>
    </Grid>
  );

  const onFormSubmit = () => {
    setIsSearching(true);
    if (date === "CUS" && (!toDate || !fromDate)) {
      return;
    }
    if (pickupDate === "CUS" && (!pickupToDate || !pickupFromDate)) {
      return;
    }
    //// on filter form submit button updating redux store for manifest filter form
    //// maintaining same data for filter search, date and terminal picker
    if (props.onFilterSubmit) {
      props.onFilterSubmit();
    }

    const filterFormValues: IFilterForm = {
      search: filterForm.search,
      date,
      toDate,
      fromDate,
      pickupDate,
      pickupFromDate,
      pickupToDate,
      origin: tid,
      destination: did,
      reporting: rTid,
      osdType: exco,
      commodityType: ctype,
      description: description?.toString()!,
      shipperName: shipperName,
      consigneeName: consigneeName,
      dockWorkerName: dockWorkerName,
      driverName: driverName,
      proNumber: proEntry?.toString()!,
      od400ProNumber: od400ProEntry?.toString()!,
      osdNumber: osdNumber?.toString()!,
      values: value,
      status,
      entryUser: entryUserObject?.nameFullLc
        ? entryUserObject
        : { as400Id: entryUser, nameFullLc: "" },
      assignedTo: assignUserObject?.as400Id
        ? assignUserObject
        : { as400Id: "", nameFullLc: assignUser },
      assignedBy: assignByObject?.as400Id
        ? assignByObject
        : { as400Id: "", nameFullLc: assignBy },
      clearingCode,
      clearingStatusFilter,
      visibility,
    };

    const page = { pageSize: pageObject?.pageSize ?? 25, page: 0 };

    const tabActions: Partial<Record<ETab, (page?: any) => void>> = {
      [ETab.overages]: () => {
        dispatch(saveOverageListPageObject(page));
        dispatch(saveOveragesFilterForm(filterFormValues));
      },
      [ETab.myAssignments]: () => {
        dispatch(saveMyAssignmentPageObject(page));
        dispatch(saveMyAssignmentsFilterForm(filterFormValues));
      },
      [ETab.shortages]: () => {
        dispatch(saveShortagesPageObject(page));
        dispatch(saveShortagesFilterForm(filterFormValues));
      },
      [ETab.damages]: () => {
        dispatch(saveDamagesPageObject(page));
        dispatch(saveDamagesFilterForm(filterFormValues));
      },
      [ETab.onHandDelivery]: () => {
        dispatch(saveOnHandDeliveryPageObject(page));
        dispatch(saveOnHandDeliveryFilterForm(filterFormValues));
      },
      [ETab.misdelivery]: () => {
        dispatch(saveMisdeliveryPageObject(page));
        dispatch(saveMisdeliveryFilterForm(filterFormValues));
      },
      [ETab.dashboard]: () => {
        dispatch(saveSearchExceptionPageObject(page));
        dispatch(saveExceptionSearchFilterForm(filterFormValues));
      },
      [ETab.cleared]: () => {
        dispatch(saveClearedPageObject(page));
        dispatch(saveClearedFilterForm(filterFormValues));
      },
    };

    // Execute the right set of actions if the tab exists
    tabActions[tabPosition]?.();
  };

  useEffect(() => {
    const mapping: Partial<Record<keyof IFilterForm, DType>> = {
      entryUser: DType.entryBy,
      assignedTo: DType.assignTo,
      assignedBy: DType.assignBy,
      proNumber: DType.pro,
      od400ProNumber: DType.od400,
      osdNumber: DType.osd,
    };

    Object.entries(mapping).forEach(([key, dtype]) => {
      const value = filterForm[key as keyof typeof filterForm];
      if (value) {
        onInputChange(value, dtype);
      }
    });
    // Set all filter form values from redux and maintain data as last visit of page filter with same session
    setDate(filterForm.date!);
    setTid(filterForm.origin!);
    setDid(filterForm.destination!);
    setRTid(filterForm.reporting!);
    setExco(filterForm.osdType!);
    setCtype(filterForm.commodityType!);
    setValue(filterForm.values!);
    setStatus(filterForm.status!);
    setToDate(filterForm.toDate ?? "");
    setFromDate(filterForm.fromDate ?? "");
    setPickupDate(filterForm.pickupDate ?? "");
    setPickupFromDate(filterForm.pickupFromDate ?? "");
    setPickupToDate(filterForm.pickupToDate ?? "");
    setDescription(
      filterForm.description ? filterForm?.description.trim() : "",
    );
    setConsigneeName(
      filterForm.consigneeName ? filterForm?.consigneeName.trim() : "",
    );
    setDockWorkerName(
      filterForm.dockWorkerName ? filterForm?.dockWorkerName.trim() : "",
    );
    setDriverName(filterForm.driverName ? filterForm?.driverName.trim() : "");

    setShipperName(
      filterForm.shipperName ? filterForm?.shipperName.trim() : "",
    );
    setProEntry(filterForm.proNumber);
    setOD400ProEntry(filterForm.od400ProNumber);
    setOSDNumber(filterForm.osdNumber);
    setEntryUserObject(filterForm.entryUser);
    setAssignUserObject(filterForm.assignedTo);
    setAssignByObject(filterForm.assignedBy);
    setClearingCode(Array.isArray(filterForm.clearingCode) ? filterForm.clearingCode : []);
    setClearingStatusFilter(Array.isArray(filterForm.clearingStatusFilter) ? filterForm.clearingStatusFilter : ["uncleared"]);
    setVisibility(Array.isArray(filterForm.visibility) ? filterForm.visibility : ["nonCleared"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabPosition, filterForm]);

  useEffect(() => {
    if (!commodityTypeMaster?.length) {
      getSearchFiltersAPI("commodityType");
    }
    if (!clearingCodesMasterData?.length) {
      loadClearingCodes();
    }
    if (!exceptionCodes?.length) {
      getSearchFiltersAPI("Exception_Codes");
    }
    if (!overagesFilterDateData?.length) {
      getSearchFiltersAPI("overageSearchDateRange");
    }
    if (!valueRangeMasterData?.length) {
      getSearchFiltersAPI("overageSearchValues");
    }
    if (!investigationStatusMasterData?.length) {
      getSearchFiltersAPI(
        STATUS_TYPE_TO_TAB_MAPPING[tabPosition] ??
        "myAssignmentStatus",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="parent-filter">
      <div className="filter">
        <Grid container spacing={2} className="filter-margin">
          <DateRangeFilter
            label="Entry Date"
            dateValue={date}
            fromDate={fromDate}
            toDate={toDate}
            dateOptions={overagesFilterDateData}
            onDateChange={(value) => {
              setFromDate("");
              setToDate("");
              setIsSearching(false);
              setDate(value);
            }}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            isSearching={isSearching}
            dateTestId="date-select"
            fromDateTestId="from-date-picker"
            toDateTestId="to-date-picker"
          />
          <DateRangeFilter
            label="Pickup Date"
            dateValue={pickupDate}
            fromDate={pickupFromDate}
            toDate={pickupToDate}
            dateOptions={overagesFilterDateData}
            onDateChange={(value) => {
              setPickupFromDate("");
              setPickupToDate("");
              setIsSearching(false);
              setPickupDate(value);
            }}
            onFromDateChange={setPickupFromDate}
            onToDateChange={setPickupToDate}
            isSearching={isSearching}
            dateTestId="pickup-date-select"
            fromDateTestId="pickup-from-date-picker"
            toDateTestId="pickup-to-date-picker"
          />
          <TerminalAutocomplete
            tabPosition={tabPosition}
            tid={tid}
            setTid={setTid}
            did={did}
            setDid={setDid}
            rTid={rTid}
            setRTid={setRTid}
          />
          {(!isMSD && !isOH) && <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={FORM_STYLES.LABEL}
                shrink={true}
                id="exceptionCode-label"
                htmlFor="exceptionCode"
              >
                Exception Code
              </InputLabel>
              <Select
                labelId="exceptionCode-label"
                id="exceptionCode"
                multiple
                className="select"
                value={exco}
                onChange={handleChangeExcep}
                style={FORM_STYLES.LABEL}
                input={<OutlinedInput label="Tag" />}
                renderValue={(selected) => selected.join(", ")}
                IconComponent={CustomIconComponent}
              >
                {exceptionCodes.map((obj) => {
                  const isPermissionDenied = !hasPermissionForExceptionCode(obj);

                  return (
                    <MenuItem
                      key={obj}
                      className="font-override"
                      value={obj}
                      title={isPermissionDenied ? "No permission" : undefined}
                      sx={isPermissionDenied ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                    >
                      <Checkbox
                        checked={exco.includes(obj)}
                        disabled={isPermissionDenied}
                      />
                      <ListItemText className="font-override" primary={obj} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          }
          {!isMSD && (
            <Grid item {...S_GRID_PROPS} className="hide-no-option">
              <FormControl fullWidth size="small">
                <InputLabel
                  style={FORM_STYLES.LABEL}
                  shrink={true}
                  htmlFor="commodityType"
                >
                  Commodity
                </InputLabel>
                <Autocomplete
                  id="commodityType"
                  key="search-ctype"
                  className="custom-autocomplete"
                  disablePortal
                  multiple
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : `${option.displayValue} - ${option.code}`
                  }
                  value={commodityTypeMaster.filter((item) =>
                    ctype.includes(item.displayValue),
                  )}
                  inputValue={cInputValue}
                  onClose={(e) => {
                    setCInputValue("");
                  }}
                  onInputChange={(e, v, r) =>
                    onAutoInputChange(v, r, "commodity")
                  }
                  forcePopupIcon={false}
                  options={commodityTypeMaster}
                  isOptionEqualToValue={(option, value) =>
                    option.displayValue === value.displayValue
                  }
                  disableCloseOnSelect
                  onChange={(e, v) => {
                    handleChangeCommodityType(
                      v.map((item) => item.displayValue),
                    );
                    setCInputValue("");
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      title={`${option.displayValue} - ${option.code}`}
                    >
                      <Checkbox checked={ctype.includes(option.displayValue)} />
                      {`${option.displayValue} - ${option.code}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      autoComplete="off"
                      {...params}
                      variant="outlined"
                    />
                  )}
                  renderTags={(value) => (
                    <div
                      className="auto-complete-selected-container"
                      title={value
                        .map((item) => `${item.displayValue} - ${item.code}`)
                        .join(", ")}
                    >
                      {value
                        .map((item) => `${item.displayValue} - ${item.code}`)
                        .join(", ")}
                    </div>
                  )} // Add renderTags property to customize rendering of selected values
                />
              </FormControl>
            </Grid>
          )}
          {renderTextField(
            "Description",
            "description",
            description,
            setDescription,
          )}
          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true} htmlFor="pro">
                PRO #
              </InputLabel>
              <Autocomplete
                id="pro"
                key="search-proEntry"
                disablePortal
                noOptionsText={noOptionsText(
                  proEntry!,
                  loading,
                  EAutoSearchFieldName.pro,
                )}
                size="small"
                value={proEntry}
                onInputChange={(e, v) => {
                  if (typeof v !== "string") {
                    return;
                  }
                  if (v.length <= MAX_LENGTH_PRO) {
                    // Restrict input to maxLength
                    onInputChange(v, DType.pro);
                  }
                }}
                onChange={(e, v, r) => onPROentryChange(v, r)}
                forcePopupIcon={false}
                className="autocomplete"
                getOptionLabel={(option) => option}
                inputValue={proEntry!}
                options={proMasterData}
                renderInput={(params) => (
                  <TextField
                    onKeyDown={handleTextFieldKeyDown}
                    onPaste={handlePaste}
                    autoComplete="off"
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          {FILTER_SHOW_OD400.includes(tabPosition) && (
            <Grid item {...S_GRID_PROPS}>
              <FormControl fullWidth size="small">
                <InputLabel
                  style={FORM_STYLES.LABEL}
                  shrink={true}
                  id="od400Pro-label"
                  htmlFor="od400Pro"
                >
                  OD400 PRO #
                </InputLabel>
                <Autocomplete
                  id="od400Pro"
                  key="search-od400ProEntry"
                  disablePortal
                  noOptionsText={noOptionsText(
                    od400ProEntry!,
                    loading,
                    EAutoSearchFieldName.od400,
                  )}
                  size="small"
                  value={od400ProEntry}
                  onInputChange={(e, v) => {
                    if (typeof v !== "string") {
                      return;
                    }
                    if (v.length <= MAX_LENGTH_PRO) {
                      onInputChange(v, DType.od400);
                    }
                  }}
                  onChange={(e, v, r) => onOD400PROentryChange(v, r)}
                  forcePopupIcon={false}
                  className="autocomplete"
                  getOptionLabel={(option) => option || ""}
                  inputValue={od400ProEntry!}
                  options={od400ProMasterData}
                  renderInput={(params) => (
                    <TextField
                      onKeyDown={handleTextFieldKeyDown}
                      onPaste={handlePaste}
                      autoComplete="off"
                      {...params}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          )}{" "}
          {!isMSD &&
            renderTextField(
              "Shipper Name",
              "shipperName",
              shipperName,
              setShipperName,
            )}
          {!isMSD &&
            renderTextField(
              "Consignee Name",
              "consigneeName",
              consigneeName,
              setConsigneeName,
            )}
          <Grid item {...S_GRID_PROPS}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={FORM_STYLES.LABEL}
                shrink={true}
                id="osdNumber-label"
                htmlFor="osdNumber"
              >
                Exception #
              </InputLabel>
              <Autocomplete
                id="osdNumber"
                disablePortal
                size="small"
                noOptionsText={noOptionsText(
                  osdNumber!,
                  loading,
                  EAutoSearchFieldName.osdNumber,
                )}
                key="search-osdNumber"
                value={osdNumber}
                onInputChange={(e, v) => {
                  if (typeof v !== "string") {
                    return;
                  }
                  if (v.length <= MAX_LENGTH_MANIFEST_NUMBER) {
                    onInputChange(v, DType.osd);
                  }
                }}
                onChange={(e, v, r) => onOSDexceptionChange(v, r)}
                forcePopupIcon={false}
                className="autocomplete"
                inputValue={osdNumber!}
                getOptionLabel={(option) => option}
                options={osdMasterData}
                renderInput={(params) => (
                  <TextField
                    onKeyDown={handleTextFieldKeyDown}
                    onPaste={handlePaste}
                    autoComplete="off"
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          {!isMSD && (
            <Grid item {...S_GRID_PROPS}>
              <FormControl fullWidth size="small">
                <InputLabel
                  style={FORM_STYLES.LABEL}
                  shrink={true}
                  id="value-label"
                  htmlFor="value"
                >
                  Value Range
                </InputLabel>
                <Select
                  labelId="value-label"
                  id="value"
                  multiple
                  className="select"
                  value={value}
                  onChange={handleChangeValue}
                  style={FORM_STYLES.LABEL}
                  input={<OutlinedInput label="Tag" />}
                  renderValue={(selected) => selected.join(", ")}
                  IconComponent={CustomIconComponent}
                >
                  {valueRangeMasterData.map((obj: IFilterDropdown) => (
                    <MenuItem
                      className="font-override"
                      key={obj.displayValue}
                      value={obj.actualValue}
                    >
                      <Checkbox checked={value.includes(obj.actualValue)} />
                      <ListItemText
                        className="font-override"
                        primary={obj.displayValue}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {tabPosition !== ETab.cleared && (
            <Grid item {...S_GRID_PROPS}>
              <FormControl fullWidth size="small">
                <InputLabel
                  style={FORM_STYLES.LABEL}
                  shrink={true}
                  id="status-label"
                  htmlFor="status"
                >
                  Status
                </InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  multiple
                  className="select"
                  value={status}
                  onChange={handleStatusChange}
                  style={FORM_STYLES.LABEL}
                  input={<OutlinedInput label="Tag" />}
                  renderValue={(selected) =>
                    investigationStatusMasterData
                      .filter((obj) => selected.includes(obj.actualValue))
                      .map((obj) => obj.displayValue)
                      .join(", ")
                  }
                  IconComponent={CustomIconComponent}
                >
                  {investigationStatusMasterData.map((obj: IFilterDropdown) => (
                    <MenuItem
                      className="font-override"
                      key={obj.displayValue}
                      value={obj.actualValue}
                    >
                      <Checkbox checked={status.includes(obj.actualValue)} />
                      <ListItemText
                        className="font-override"
                        primary={obj.displayValue}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {/* Only show for ETab.cleared, last in form */}
          {isCleared && (
              <Grid item {...S_GRID_PROPS}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    style={FORM_STYLES.LABEL}
                    shrink={true}
                    htmlFor="clearingCode"
                  >
                    Clearing Code
                  </InputLabel>
                  <Autocomplete
                    id="clearingCode"
                    key="search-clearingCode"
                    className="custom-autocomplete"
                    disablePortal
                    multiple
                    options={clearingCodesMasterData ?? []}
                    value={selectedClearingCodes}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.displayValue
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.actualValue === value.actualValue
                    }
                    disableCloseOnSelect
                    onChange={(e, selected) => {
                      const codes = (selected ?? [])
                        .map((opt) => opt.actualValue)
                        .filter(Boolean);
                      setClearingCode(codes);
                    }}
                    renderOption={(props, option) => (
                      <li {...props} title={option.displayValue}>
                        <Checkbox
                          checked={clearingCode.includes(option.actualValue)}
                        />
                        {option.displayValue}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        autoComplete="off"
                        {...params}
                        data-testid="clearingCode"
                        variant="outlined"
                      />
                    )}
                    renderTags={(value) => (
                      <div
                        className="auto-complete-selected-container"
                        title={value.map((v) => v.displayValue).join(", ")}
                      >
                        {value.map((v) => v.displayValue).join(", ")}
                      </div>
                    )}
                  />
                </FormControl>
              </Grid>
          )}
          <UserAutocomplete
            tabPosition={tabPosition}
            entryUser={entryUser}
            setEntryUser={setEntryUser}
            setEntryUserObject={setEntryUserObject}
            assignUser={assignUser}
            setAssignUser={setAssignUser}
            setAssignUserObject={setAssignUserObject}
            assignBy={assignBy}
            setAssignBy={setAssignBy}
            setAssignByObject={setAssignByObject}
            loading={loading}
            setLoading={setLoading}
            entryUserObject={entryUserObject}
            assignUserObject={assignUserObject}
            assignByObject={assignByObject}
          />
          {FILTER_SHOW_DRIVER.includes(tabPosition) && (
            <>
              {renderTextField(
                "Driver's Name",
                "driverName",
                driverName,
                setDriverName,
              )}
              {renderTextField(
                "Dock Worker's Name",
                "dockWorkerName",
                dockWorkerName,
                setDockWorkerName,
              )}
            </>
          )}
          {/* Clear Status (Open/Cleared) for exceptions/search only */}
          {isDashboard && (
            <Grid item {...S_GRID_PROPS}>
              <FormControl fullWidth size="small">
                <InputLabel
                  style={FORM_STYLES.LABEL}
                  shrink={true}
                  id="visibility-label"
                  htmlFor="visibility"
                >
                  Clear Status
                </InputLabel>
                <Select
                  labelId="visibility-label"
                  id="visibility"
                  multiple
                  className="select"
                  value={visibility}
                  onChange={handleVisibilityChange}
                  style={FORM_STYLES.LABEL}
                  input={<OutlinedInput label="Tag" />}
                  renderValue={(selected) => {
                    if (selected.length === 2) return "All";
                    return selected.map(val => 
                      CLEARING_STATUS_OPTIONS.find(opt => opt.value === val)?.label
                    ).join(", ");
                  }}
                  IconComponent={CustomIconComponent}
                >
                  {CLEARING_STATUS_OPTIONS.map((option) => (
                    <MenuItem
                      className="font-override"
                      key={option.value}
                      value={option.value}
                      sx={{ borderLeft: `3px solid ${option.borderColor}` }}
                    >
                      <Checkbox checked={visibility.includes(option.value)} />
                      <ListItemText className="font-override" primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid
            item
            {...XL_GRID_PROPS}
            display="flex"
            justifyContent="flex-end"
            className="filter-margin"
          >
            <Button onClick={onFormClear} variant="text" className="clear-link">
              Clear Filter
            </Button>
            <Button
              onClick={onFormSubmit}
              variant="contained"
              className="submit"
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default SearchFilterForm;
